import { filter, isFunction, isPlainObject, isString, orderBy } from 'lodash';
import { TBaseTagContext } from '../types';
import { TTag } from './types';
import React, { useEffect, useMemo, useState } from 'react';
import UniversalChildren from '../../UniversalTag/UniversalChildren';
import { validate } from '@krugom/validator';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { selectUnits } from '../../../store/selector/selectUnits';
import { TBaseDataObject } from '../../../types';
import { filtersFunction } from './filtersFinction';

export const CustomCollection: React.FC<{
  tagProps: TTag;
  context: Partial<TBaseTagContext>;
}> = React.memo(({ tagProps, context }) => {
  const validationScheme = useMemo(
    () => ({
      type: 'object',
      properties: {
        type: {
          type: 'string',
          required: true,
        },
        sorts: {
          type: 'custom',
          validator: (v: any) =>
            isPlainObject(v) && !Object.values(v).find(item => item !== 'asc' && item !== 'desc'),
        },
        filters: {
          type: 'custom',
          validator: (v: any) => isPlainObject(v) || isFunction(v),
        },
        pagination: {
          type: 'object',
          properties: {
            pageSize: {
              type: 'number',
              required: true,
            },
            start: {
              type: 'number',
            },
            end: {
              type: 'number',
            },
          },
        },
      },
    }),
    []
  );

  const result = useMemo(() => validate(tagProps, validationScheme), [tagProps, validationScheme]);

  const [filters, setFilters] = useState<TTag['filters']>(tagProps.filters);
  const [sorts, setSorts] = useState<TTag['sorts']>(tagProps.sorts);
  const defaultPagination = useMemo(
    () =>
      tagProps.pagination?.page && tagProps.pagination?.pageSize
        ? { page: tagProps.pagination.page || 1, pageSize: tagProps.pagination.pageSize || 25 }
        : undefined,
    [tagProps.pagination]
  );
  const [pagination, setPagination] = useState<{ page: number; pageSize: number } | undefined>(
    defaultPagination
  );

  useEffect(() => {
    setFilters(tagProps.filters);
    setSorts(tagProps.sorts);
    setPagination(defaultPagination);
  }, [tagProps, defaultPagination]);

  const selector = useSelector((state: RootState) => selectUnits(state, tagProps.type));
  const items = useMemo(() => {
    const srcItems: TBaseDataObject[] = Object.values(selector || {});
    const collectionFilters = filters
      ? isString(filters)
        ? filtersFunction(filters)
        : filters
      : undefined;
    const filteredItems = collectionFilters
      ? (filter(srcItems, collectionFilters) as TBaseDataObject[])
      : srcItems;
    const sortedItems = sorts
      ? (orderBy(filteredItems, [Object.keys(sorts), Object.values(sorts)]) as TBaseDataObject[])
      : filteredItems;
    const startIndex = pagination ? (pagination.page - 1) * pagination.pageSize : 0;
    const endIndex = pagination ? startIndex + pagination.pageSize : undefined;
    const pageItems = pagination ? sortedItems.slice(startIndex, endIndex) : sortedItems;
    return pageItems;
  }, [sorts, filters, pagination, tagProps.type, selector]);

  const childrenContext: Partial<TBaseTagContext> = useMemo(
    () => ({
      ...context,
      collection: {
        items,
        setSorts,
        setFilters,
        setPagination,
      },
    }),
    [items, context]
  );

  if (!result.isValid) {
    console.error(
      'Тэг: $CollectionContext;',
      `Путь: ${context.path?.join('.') || 'Не указан'};`,
      result.errors
    );
    return <UniversalChildren context={context}>{tagProps.children}</UniversalChildren>;
  }

  return <UniversalChildren context={childrenContext}>{tagProps.children}</UniversalChildren>;
});
CustomCollection.displayName = 'CustomCollection';
